import axios from "axios";
import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Table, Empty } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// TIMEZONE
import moment from "moment-timezone";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
	updateReportTableParams,
	updateTableListColumns,
	updateTableSelectedColumns,
	updateOpenDrawerRows,
	updateReportRow,
} from "../../redux/reports/reportsSlice";
// AUTHORIZATION
import validatePermission from "../../utils/validatePermissions";
import GETJwtToken from "../../redux/authentication/authentication";
// UTILS
import { getTableRowClass, generateExcelAndDownload } from "../../utils/utils";
// COMPONENTS
import ReportsAlertDownloadModal from "./ReportsAlertDownloadModal";
import ReportsDrawer from "./ReportsDrawer";
// INTERFACES
import {
	ReportsTableInterface,
	ReportsListOfColumnsInterface,
} from "../../interfaces/Reports.interface";
import { RootState } from "../../app/store";

const { Column } = Table;

function ReportsTable({
	defaultColumns,
	downloadReportData,
	response,
	triggerGetReport,
	isLoading,
	isFetching,
	isError,
	useFilterTarget,
	usePagination,
}: ReportsTableInterface) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const [t] = useTranslation("global");
	const dispatch = useDispatch();
	const CUSTOM_COLUM_NAMES: any = {
		up_time_target: "AVAILABILITY (%)",
		unavailability: "UNAVAILABILITY (%)",
	};

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [data, setData] = useState<any>([]);
	const [downloadReport, setDownloadReport] = useState(false);
	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);
	const { permissions } = useSelector((state: RootState) => state.user);
	const {
		reportTableParams,
		reportRefreshTable,
		filterExclusionSaturdays,
		filterExclusionSundays,
		filterExclusionHolidays,
		filterLiftType,
		filterAddExclusionTimeRanges,
		filterExclusionTimeRange,
		filterCascaderTypeList,
		filterStartDate,
		filterEndDate,
		filterTimezone,
		tableSelectedColumns,
		filterExclusionDateRange,
		filterAddExclusionDateRanges,
		filterUseCustomSearch,
	} = useSelector((state: RootState) => state.report);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// FUNCTIONS ************************************** */

	/** Take all the values that comes from report API call and create
  a list with all the available columns */
	const buildListOfColumnsArray = () => {
		const newArr: ReportsListOfColumnsInterface[] = [];
		Object.keys(data[0]).forEach((key: any) => {
			const isArrayValidation = Array.isArray(data[0][key]);
			if (!(typeof data[0][key] === "object" || isArrayValidation)) {
				newArr.push({
					label: key,
					value: key,
				});
			}
			return true;
		});
		dispatch(updateTableListColumns(newArr));
		dispatch(updateTableSelectedColumns(defaultColumns));
	};

	const getSearchAndFilters = () => {
		let searchAndFilterString = "";
		// FILTER START DATE
		if (filterStartDate !== "") {
			searchAndFilterString = searchAndFilterString.concat(
				"start_date=",
				filterStartDate,
				"T00:00:00.000Z"
			);
		}
		// FILTER END DATE
		if (filterEndDate !== "") {
			searchAndFilterString = searchAndFilterString.concat(
				"&end_date=",
				filterEndDate,
				"T23:59:59.999Z"
			);
		}
		// FILTER EXCLUSION START DATE / END DATE
		if (
			filterExclusionDateRange &&
			filterExclusionDateRange.length > 0 &&
			filterAddExclusionDateRanges
		) {
			searchAndFilterString = searchAndFilterString.concat("&exclusion_list=");
			let exclusiontDateList = "";
			filterExclusionDateRange.map(
				(exclusionDate: string, exclusionDateIndex: number) => {
					const item1 = exclusionDate[0].split(" ");
					const item2 = exclusionDate[1].split(" ");
					exclusiontDateList = exclusiontDateList.concat(
						`${item1[0]}T${item1[1]}:00.000Z,`,
						`${item2[0]}T${item2[1]}:59.999Z`,
						`${
							exclusionDateIndex < filterExclusionDateRange.length - 1
								? ";"
								: ""
						}`
					);
					return true;
				}
			);
			exclusiontDateList = exclusiontDateList
				.replaceAll("/", "-")
				.replaceAll(":", "%3A")
				.replaceAll(",", "%2C")
				.replaceAll(";", "%3B");
			searchAndFilterString = searchAndFilterString.concat(exclusiontDateList);
		}
		// FILTER EXCLUSION START TIME / END TIME
		if (
			filterExclusionTimeRange &&
			filterExclusionTimeRange.length > 0 &&
			filterAddExclusionTimeRanges
		) {
			searchAndFilterString = searchAndFilterString.concat(
				"&exclusion_daily_list="
			);
			let exclusiontTimeList = "";
			filterExclusionTimeRange.map(
				(exclusionTime: string, exclusionTimeIndex: number) => {
					const item1 = exclusionTime[0].split(" ");
					const item2 = exclusionTime[1].split(" ");
					exclusiontTimeList = exclusiontTimeList.concat(
						`${item1[0]},`,
						`${item2[0]}`,
						`${
							exclusionTimeIndex < filterExclusionTimeRange.length - 1
								? ";"
								: ""
						}`
					);
					return true;
				}
			);
			exclusiontTimeList = exclusiontTimeList
				.replaceAll(":", "%3A")
				.replaceAll(",", "%2C")
				.replaceAll(";", "%3B");
			searchAndFilterString = searchAndFilterString.concat(exclusiontTimeList);
		}
		// FILTER EXCLUSION SATURDAY
		if (filterExclusionSaturdays) {
			searchAndFilterString = searchAndFilterString.concat(
				"&exclude_saturdays=true"
			);
		}
		// FILTER EXCLUSION SUNDAYS
		if (filterExclusionSundays) {
			searchAndFilterString = searchAndFilterString.concat(
				"&exclude_sundays=true"
			);
		}
		// FILTER EXCLUSION HOLIDAYS
		if (filterExclusionHolidays) {
			searchAndFilterString = searchAndFilterString.concat(
				"&exclude_holidays=true"
			);
		}
		// FILTER OFFSET TYPE
		if (
			downloadReportData.name === "generalReport" ||
			downloadReportData.name === "faultReport" ||
			downloadReportData.name === "trafficReport" ||
			downloadReportData.name === "carUsageReport" ||
			downloadReportData.name === "serviceReport"
		) {
			// FILTER OFFSET TYPE
			if (filterCascaderTypeList && filterCascaderTypeList.length > 0) {
				searchAndFilterString = searchAndFilterString.concat(
					`&filter_target=msg.type%7C${
						filterUseCustomSearch.type === "Banks" ? "bank_id" : ""
					}${filterUseCustomSearch.type === "Servers" ? "server_id" : ""}${
						filterUseCustomSearch.type === "Organizations" ? "org_id" : ""
					}${filterUseCustomSearch.type === "Lifts" ? "lift_id" : ""}&filter=`
				);
				filterCascaderTypeList.map(
					(offsetType: string, offsetTypeIndex: number) => {
						searchAndFilterString = searchAndFilterString.concat(
							`${offsetType}${
								offsetTypeIndex + 1 < filterCascaderTypeList.length ? "," : ""
							}`
						);
						return true;
					}
				);
				searchAndFilterString = searchAndFilterString.concat(
					`%7C${filterUseCustomSearch.value}`
				);
			} else {
				searchAndFilterString = searchAndFilterString.concat(
					`&filter_target=${
						filterUseCustomSearch.type === "Banks" ? "bank_id" : ""
					}${filterUseCustomSearch.type === "Servers" ? "server_id" : ""}${
						filterUseCustomSearch.type === "Organizations" ? "org_id" : ""
					}${filterUseCustomSearch.type === "Lifts" ? "lift_id" : ""}&filter=${
						filterUseCustomSearch.value
					}`
				);
			}
		} else if (
			downloadReportData.name === "beneficialReport" ||
			downloadReportData.name === "performanceReport" ||
			downloadReportData.name === "outOfServiceReport"
		) {
			if (filterCascaderTypeList && filterCascaderTypeList.length > 0) {
				searchAndFilterString = searchAndFilterString.concat(
					"&filter_target=msg.type&filter="
				);
				filterCascaderTypeList.map((offsetType: string) => {
					searchAndFilterString = searchAndFilterString.concat(
						`${offsetType}${filterCascaderTypeList.length > 1 ? "," : ""}`
					);
					return true;
				});
			}
		}
		// FILTER LIFT TYPE
		if (filterLiftType && filterLiftType !== "ALL") {
			searchAndFilterString = searchAndFilterString.concat(
				"&lift_type=",
				filterLiftType
			);
		}
		// FILTER TIME ZONE
		if (
			filterTimezone &&
			filterTimezone.tzCode &&
			filterTimezone.tzCode !== ""
		) {
			searchAndFilterString = searchAndFilterString.concat(
				"&timezone=",
				filterTimezone.tzCode
			);
		} else {
			searchAndFilterString = searchAndFilterString.concat(
				"&timezone=",
				moment.tz.guess()
			);
		}
		if (
			downloadReportData.name === "beneficialReport" ||
			downloadReportData.name === "performanceReport" ||
			downloadReportData.name === "outOfServiceReport"
		) {
			if (filterUseCustomSearch.useCustomSearch) {
				if (filterUseCustomSearch.type === "Banks") {
					searchAndFilterString = searchAndFilterString.concat(
						`${
							useFilterTarget ? "&filter_target=bank_id&filter=" : "&bank_id="
						}`,
						filterUseCustomSearch.value
					);
				} else if (filterUseCustomSearch.type === "Servers") {
					searchAndFilterString = searchAndFilterString.concat(
						`${
							useFilterTarget
								? "&filter_target=server_id&filter="
								: "&server_id="
						}`,
						filterUseCustomSearch.value
					);
				} else if (filterUseCustomSearch.type === "Organizations") {
					searchAndFilterString = searchAndFilterString.concat(
						`${useFilterTarget ? "&filter_target=org_id&filter=" : "&org_id="}`,
						filterUseCustomSearch.value
					);
				} else if (filterUseCustomSearch.type === "Lifts") {
					searchAndFilterString = searchAndFilterString.concat(
						`${
							useFilterTarget ? "&filter_target=lift_id&filter=" : "&lift_id="
						}`,
						filterUseCustomSearch.value
					);
				}
			}
		}
		//
		if (searchAndFilterString.length > 3) {
			return searchAndFilterString;
		}
		return "";
	};

	const handleTableChange = async (
		pagination: any,
		filters: any,
		sorter: any
	) => {
		if (usePagination) {
			const searchAndFilterString = getSearchAndFilters();
			dispatch(
				updateReportTableParams({
					pagination,
					filters,
					...sorter,
				})
			);
			//
			const token: string = await GETJwtToken();
			triggerGetReport({
				page: pagination.current,
				limit: pagination.pageSize,
				token: token || "",
				searchAndFilterString,
			});
		}
	};

	const refreshTable = async () => {
		const searchAndFilterString = getSearchAndFilters();
		dispatch(
			updateReportTableParams({
				pagination: {
					current: 1,
					pageSize: 10,
					total: reportTableParams.pagination.total,
				},
			})
		);
		//
		const token: string = await GETJwtToken();
		triggerGetReport({
			page: 1,
			limit: 10,
			token,
			searchAndFilterString,
		});
	};

	const formatDateColumn = (value: string) =>
		moment(value)
			.tz(
				filterTimezone && filterTimezone.tzCode && filterTimezone.tzCode !== ""
					? filterTimezone.tzCode
					: moment.tz.guess()
			)
			.format("MMMM Do YYYY, h:mm:ss a");

	const buildDataObject = (newData: any) => {
		const newArr: any = [];
		const formatDates = [
			"created_at",
			"synced_at",
			"start_timestamp",
			"end_timestamp",
			"timestamp",
			"out_of_service_timestamp",
		];
		newData.map((record: any) => {
			const newObject = { ...record };
			// Format dates
			formatDates.map((d: string) => {
				if (record[d]) {
					if (record[d] !== "0001-01-01T00:00:00Z" && record[d] !== "-") {
						newObject[d] = formatDateColumn(record[d]);
					} else {
						newObject[d] = "--";
					}
				}
				return true;
			});
			if (record.msg) {
				Object.keys(record.msg).forEach((key: any) => {
					newObject[key] = record.msg[key];
					return true;
				});
			}
			newArr.push(newObject);
			return true;
		});
		setData(newArr);
	};

	const dinamicColumnTitle = (title: string) => {
		if (CUSTOM_COLUM_NAMES[title]) {
			return CUSTOM_COLUM_NAMES[title].toUpperCase();
		}
		return title.toUpperCase().replaceAll("_", " ");
	};

	const resetData = () => {
		setData([]);
		dispatch(updateTableSelectedColumns([]));
		dispatch(updateTableListColumns([]));
		dispatch(
			updateReportTableParams({
				pagination: {
					current: 1,
					pageSize: 10,
					total: 0,
				},
			})
		);
	};

	const buildAndDownloadReport = async () => {
		const token: string = await GETJwtToken();
		let searchAndFilterString = getSearchAndFilters();
		searchAndFilterString = searchAndFilterString.concat("&download=true");
		searchAndFilterString = searchAndFilterString.concat(
			`&download_headers=${tableSelectedColumns.toString()}`
		);
		setTimeout(() => {
			setDownloadReport(false);
		}, 1000);
		// *******************************************************
		// Its important to set the 'Content-Type': 'blob' and responseType:'arraybuffer'.
		// eslint-disable-next-line no-return-await
		return await axios
			.get(
				`${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/lift-records/${
					downloadReportData.serviceName
				}?${searchAndFilterString || ""}`,
				{
					responseType: "blob",
					headers: {
						Authorization: token,
						"Content-type": "application/json",
					},
				}
			)
			.then((res: any) => {
				const data1 = new Blob([res?.data], {
					type: "application / vnd. MS Excel",
				});
				const csvURL = window.URL.createObjectURL(data1);
				const tempLink = document.createElement("a");
				tempLink.href = csvURL;

				tempLink.setAttribute(
					"download",
					`${downloadReportData.name}-${moment()
						.tz(
							filterTimezone &&
								filterTimezone.tzCode &&
								filterTimezone.tzCode !== ""
								? filterTimezone.tzCode
								: moment.tz.guess()
						)
						.format("MMMM Do YYYY, h:mm:ss a")}.csv`
				);
				tempLink.click();
			});
	};

	const onCellNameClick = (record: any) => {
		if (record) {
			dispatch(updateOpenDrawerRows(true));
			dispatch(updateReportRow(record));
		}
	};

	const checkVariableIsNotObjectOrArray = (value: any) => {
		let isValid = true;
		if (Array.isArray(value)) {
			isValid = false;
		}
		if (typeof value === "object" && value !== null && !Array.isArray(value)) {
			isValid = false;
		}
		return isValid;
	};

	const buildAndDownloadReportServiceInterruption = () => {
		const newArr: any = [];
		if (data && data.length > 0) {
			data.map((row: any) => {
				if (
					row &&
					row.service_interruptions &&
					row.service_interruptions.length > 0
				) {
					row.service_interruptions.map((serviceInterruption: any) => {
						const newObject: any = {};
						Object.entries(row).map(([key, value]: any) => {
							if (
								checkVariableIsNotObjectOrArray(value) &&
								key !== "organization_name" &&
								key !== "organization_id"
							) {
								if (typeof value === "string") {
									newObject[key] = value.replaceAll(",", "");
								} else {
									newObject[key] = value;
								}
							}
							return true;
						});
						newObject.bank_name =
							serviceInterruption.bank_name.replaceAll(",", "") || "--";
						newObject.lift_name =
							serviceInterruption.lift_name.replaceAll(",", "") || "--";
						newObject.floor = serviceInterruption.floor || "--";
						newObject.message =
							serviceInterruption.message.replaceAll(",", "") || "--";
						newObject.short_message =
							serviceInterruption.short_message.replaceAll(",", "") || "--";
						newObject.timestamp =
							serviceInterruption.timestamp.replaceAll(",", "") || "--";
						newObject.duration = `${
							serviceInterruption.duration.days >= 0
								? `Days: ${serviceInterruption.duration.days} `
								: "-"
						} ${
							serviceInterruption.duration.hours >= 0
								? `Hours: ${serviceInterruption.duration.hours} `
								: "-"
						} ${
							serviceInterruption.duration.minutes >= 0
								? `Minutes: ${serviceInterruption.duration.minutes} `
								: "-"
						} ${
							serviceInterruption.duration.seconds >= 0
								? `Seconds: ${serviceInterruption.duration.seconds} `
								: "-"
						} ${
							serviceInterruption.duration.milliseconds >= 0
								? `Miliseconds: ${serviceInterruption.duration.milliseconds} `
								: "-"
						}`;
						newArr.push(newObject);
						return true;
					});
				} else {
					const newObject: any = {};
					Object.entries(row).map(([key, value]: any) => {
						if (
							checkVariableIsNotObjectOrArray(value) &&
							key !== "organization_name" &&
							key !== "organization_id"
						) {
							if (typeof value === "string") {
								newObject[key] = value.replaceAll(",", "");
							} else {
								newObject[key] = value;
							}
						}
						return true;
					});
					newObject.bank_name = "--";
					newObject.lift_name = "--";
					newObject.floor = "--";
					newObject.message = "--";
					newObject.short_message = "--";
					newObject.timestamp = "--";
					newObject.duration = "--";
					newArr.push(newObject);
				}
				return true;
			});
		}
		if (newArr.length > 0) {
			generateExcelAndDownload(
				newArr,
				`${downloadReportData.name} ${moment()
					.tz(
						filterTimezone &&
							filterTimezone.tzCode &&
							filterTimezone.tzCode !== ""
							? filterTimezone.tzCode
							: moment.tz.guess()
					)
					.format("MMMM Do YYYY, h:mm:ss a")}`
			);
		}
		setTimeout(() => {
			setDownloadReport(false);
		}, 1000);
	};
	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		resetData();
		return () => {
			resetData();
		};
	}, []);

	useEffect(() => {
		if (data && data.length > 0) {
			buildListOfColumnsArray();
		}
	}, [data]);

	useEffect(() => {
		if (response && response.data && response.data.length > 0) {
			if (usePagination) {
				dispatch(
					updateReportTableParams({
						...reportTableParams,
						pagination: {
							...reportTableParams.pagination,
							total: response.total,
						},
					})
				);
			}
			buildDataObject(response.data);
		} else {
			resetData();
		}
	}, [response]);

	useEffect(() => {
		if (isError) {
			setData([]);
		}
	}, [isError]);

	useEffect(() => {
		if (downloadReport) {
			if (
				downloadReportData.name === "beneficialReport" ||
				downloadReportData.name === "performanceReport"
			) {
				buildAndDownloadReportServiceInterruption();
			} else {
				buildAndDownloadReport();
			}
		}
	}, [downloadReport]);

	useEffect(() => {
		if (reportRefreshTable) {
			refreshTable();
		}
	}, [reportRefreshTable]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			{validatePermission("performance-report", permissions) ? (
				<>
					{/** ******************************************* */}
					{/** TABLE */}
					<Table
						rowClassName={(record, index) => getTableRowClass(index, theme)}
						dataSource={data}
						size='small'
						className='mt-3'
						pagination={
							usePagination
								? reportTableParams.pagination
								: {
										defaultPageSize: 10,
										showSizeChanger: true,
										pageSizeOptions: ["8", "20", "50", "100"],
								  }
						}
						onChange={handleTableChange}
						scroll={{ x: 900 }}
						loading={isLoading || isFetching}
					>
						{/** ROW NO */}
						<Column
							onCell={(record) => ({
								onClick: () => {
									onCellNameClick(record);
								},
							})}
							className='generalStyles__tableHeaderLink'
							title='No.'
							render={(text: string, record: any, index: number) => (
								<div className=''>{index + 1 || ""}</div>
							)}
						/>
						{/** DINAMIC COLUMNS */}
						{tableSelectedColumns.map((column: string, indexColumn: number) => (
							<Column
								onCell={(record) => ({
									onClick: () => {
										onCellNameClick(record);
									},
								})}
								title={dinamicColumnTitle(column)}
								dataIndex={column}
								key={indexColumn}
								render={(text: string) => (
									<div className=''>{text || "--"}</div>
								)}
							/>
						))}
					</Table>
				</>
			) : (
				<>
					<div className='generalStyles__noAccessToListTable'>
						<Empty
							description={t(
								"organizationManagement.listNotAvailableOrganizations"
							)}
							image={Empty.PRESENTED_IMAGE_SIMPLE}
						/>
					</div>
				</>
			)}
			{/** ******************************************* */}
			{/** ALERT DOWNLOAD REPORT */}
			<ReportsAlertDownloadModal setDownloadReport={setDownloadReport} />
			{/** ******************************************* */}
			{/** REPORT DRAWER BY ROW */}
			<ReportsDrawer />
		</div>
	);
}

export default ReportsTable;
